import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Divider, Form } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { InputNumber } from 'shared/ui/InputNumber';
import { ModernSelect as Select } from 'shared/ui/ModernSelect';
import { useGetCurrencySymbol } from 'app/appState';
import { TextArea } from 'shared/ui/TextArea';
import { useGetAllWarehousesQuery } from 'entities/Warehouse';
import { SizeCodeForListing, SizeCodeType, SizeGroup, useCreateSizeCodeMutation, useUpdateSizeCodeMutation } from 'entities/SizeCode';
import { showNotification } from 'app/providers/NotificationsProvider';
import { getLocalizedString, validateLocalizedJSON } from 'shared/utils/helpers/JSONLocalization';
import { DrawerMode } from '../model/types';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { UserPermissions } from 'entities/User';
import { useDefaultLocalizedDescription } from 'shared/utils/hooks/useDefaultLocalizedDescription';
import { useGetRentOptionsQuery } from 'entities/RentOption';
import { CheckboxGroup } from 'shared/ui/CheckboxGroup';
import { globalConfirmModalActions } from 'app/globalConfirmModal';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';
import { checkIsRateChanged } from '../utils/helpers/checkIsRateChanged';

interface SizeCodeFormProps {
  sizeCode?: Nullable<SizeCodeForListing>;
  mode: Nullable<DrawerMode>;
  handleClose: () => void;
}

export const SizeCodeForm: FC<SizeCodeFormProps> = memo((props) => {
  const { sizeCode, mode, handleClose } = props;
  const { t } = useAppTranslation(['boxes', 'common']);

  const dispatch = useAppDispatch();

  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>();

  const [form] = Form.useForm();

  const currencySymbol = useGetCurrencySymbol();
  const defaultLocalizedDescription = useDefaultLocalizedDescription();

  const isForbidden = useCheckPermission(UserPermissions.EDIT_UNITS);

  const { data: rentOptions } = useGetRentOptionsQuery(
    { warehouseId: selectedWarehouseId, showAsPromotion: true },
    { skip: !selectedWarehouseId },
  );

  const [createSizeCode, { isLoading: isCreationLoading }] = useCreateSizeCodeMutation();
  const [updateSizeCode, { isLoading: isUpdationLoading }] = useUpdateSizeCodeMutation();

  const { data: warehouses } = useGetAllWarehousesQuery();

  const isLoading = isUpdationLoading || isCreationLoading;

  const warehouseOptions = useMemo(() => {
    return (
      warehouses?.map((warehouse) => ({
        label: warehouse.name,
        value: warehouse.warehouseId,
      })) || []
    );
  }, [warehouses]);

  const rentOptionOptions = useMemo(() => {
    const filteredRentOptionsByDiscountKeys = rentOptions?.filter((rentOption) => rentOption.discountKeys?.length) || [];

    return filteredRentOptionsByDiscountKeys.map((option) => {
      const discountKey = option.discountKeys?.[0] || option.rentOptionId;

      return {
        label: getLocalizedString(option.label),
        value: discountKey,
      };
    });
  }, [rentOptions]);

  const sizeCodeTypeOptions = Object.values(SizeCodeType).map((type) => ({
    label: type,
    value: type,
  }));

  const sizeCodeGroupOptions = Object.values(SizeGroup).map((group) => ({
    label: group,
    value: group,
  }));

  useEffect(() => {
    if (sizeCode) {
      const discountKeys = sizeCode.rentOptions?.map((option) => option.discountKeys) || [];

      form.setFieldsValue({
        ...sizeCode,
        discountKeys,
      });

      if (sizeCode?.warehouseId) {
        setSelectedWarehouseId(sizeCode.warehouseId);
      }
    } else {
      form.resetFields();
    }
  }, [form, sizeCode]);

  const closeConfirmModal = useCallback((): void => {
    dispatch(globalConfirmModalActions.setGlobalConfirmModalData(null));
  }, [dispatch]);

  const submitUpdateSizeCode = useCallback(
    async (data: SizeCodeForListing, updateBoxesPrice?: boolean): Promise<void> => {
      if (!sizeCode) {
        return;
      }

      await updateSizeCode({
        ...data,
        sizeCodeId: sizeCode.sizeCodeId,
        updateBoxesPrice,
      }).unwrap();

      showNotification('info', t('Success', { ns: 'common' }), t('Size code has been successfully updated'));

      form.resetFields();
      handleClose();
      closeConfirmModal();
    },
    [sizeCode, updateSizeCode, t, form, handleClose, closeConfirmModal],
  );

  const showRateChangeConfirmModal = useCallback(
    (data: SizeCodeForListing): void => {
      dispatch(
        globalConfirmModalActions.setGlobalConfirmModalData({
          title: t('Rates have been changed'),
          description: t(
            'You have changed one or more rates. Would you like to update the box prices accordingly? The rate for boxes in active contracts will remain as defined in the contract!',
          ),
          isConfirmDisabled: isForbidden,
          onCancel: async () => {
            await submitUpdateSizeCode(data, false);
          },
          onOk: async () => {
            await submitUpdateSizeCode(data, true);
          },
        }),
      );
    },
    [dispatch, t, isForbidden, submitUpdateSizeCode],
  );

  const handleSubmit = useCallback(
    async (data: SizeCodeForListing): Promise<void> => {
      try {
        switch (mode) {
          case DrawerMode.SIZE_CODE_CREATE:
            await createSizeCode(data).unwrap();
            showNotification('info', t('Success', { ns: 'common' }), t('Size code has been successfully created'));

            form.resetFields();

            handleClose();
            break;
          case DrawerMode.SIZE_CODE_EDIT:
            if (sizeCode) {
              const isRateChanged = checkIsRateChanged(sizeCode, data);

              if (isRateChanged) {
                showRateChangeConfirmModal(data);
              } else {
                await submitUpdateSizeCode(data);
              }
            }
            break;
        }
      } catch (error: CustomAny) {
        console.log(error);
        showNotification(
          'error',
          t('Error', { ns: 'common' }),
          t(`Error when ${mode === DrawerMode.SIZE_CODE_CREATE ? 'creating' : 'updating'} sizeCode`),
        );
      }
    },
    [createSizeCode, form, handleClose, mode, showRateChangeConfirmModal, sizeCode, submitUpdateSizeCode, t],
  );

  const changeWarehouseId = useCallback((warehouseId: string) => {
    setSelectedWarehouseId(warehouseId);
  }, []);

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit} className="flex flex-col px-10 py-4" disabled={isForbidden}>
      <div className="flex justify-between items-center pt-4 pb-10">
        <div className="font-semibold text-3xl">
          {mode === DrawerMode.SIZE_CODE_EDIT ? `${t('Edit')} ${formatDecimal(sizeCode?.square)}` : t('Create sizeCode')}
        </div>
        <div className="text-primaryLight font-normal cursor-pointer" onClick={handleClose}>
          {t('Close', { ns: 'common' })}
        </div>
      </div>

      <Form.Item
        label={t('Warehouse')}
        name="warehouseId"
        rules={[{ required: true, message: `${t('Please, select warehouse')}!` }]}
        className="flex-1"
      >
        <Select
          options={warehouseOptions}
          placeholder={t('Please, select warehouse')}
          bordered
          disabled={mode === DrawerMode.SIZE_CODE_EDIT}
          onChange={changeWarehouseId}
        />
      </Form.Item>
      <div className="flex gap-4">
        <Form.Item
          label={t('Size code type')}
          rules={[{ required: true, message: `${t('Please, select size code type')}!` }]}
          className="flex-1"
          name="sizeCodeType"
        >
          <Select options={sizeCodeTypeOptions} placeholder={t('Size code type')} bordered />
        </Form.Item>

        <Form.Item
          label={t('Size group')}
          rules={[{ required: true, message: `${t('Please, select size group')}!` }]}
          className="flex-1"
          name="sizeGroup"
        >
          <Select options={sizeCodeGroupOptions} placeholder={t('Size group')} bordered />
        </Form.Item>
      </div>

      <Form.Item
        label={t('Square')}
        name="square"
        className="flex-1"
        rules={[{ required: true, message: `${t('Please, enter square')}!` }]}
      >
        <InputNumber placeholder={t('Square')} bordered isDisabled={mode === DrawerMode.SIZE_CODE_EDIT} />
      </Form.Item>

      <Divider className="mb-4 mt-0" />
      <div className="flex gap-4 mb-0">
        <Form.Item label={`${t('Daily rate')}/${currencySymbol}`} name="dailyRate" className="flex-1 mb-0">
          <InputNumber placeholder={t('Daily rate')} bordered />
        </Form.Item>
        <Form.Item label={`${t('Week rate')}/${currencySymbol}`} name="weekRate" className="flex-1 mb-0">
          <InputNumber placeholder={t('Week rate')} bordered />
        </Form.Item>
        <Form.Item label={`${t('Month rate')}/${currencySymbol}`} name="monthRate" className="flex-1 mb-0">
          <InputNumber placeholder={t('Month rate')} bordered />
        </Form.Item>
      </div>
      <Form.Item
        className="my-0 text-center"
        validateStatus="error"
        help={form.getFieldError(['dailyRate', 'weekRate', 'monthRate'])?.join(' ') || ''}
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.dailyRate !== currentValues.dailyRate ||
          prevValues.weekRate !== currentValues.weekRate ||
          prevValues.monthRate !== currentValues.monthRate
        }
      >
        {({ getFieldValue }) => {
          const dailyRate = getFieldValue('dailyRate');
          const weekRate = getFieldValue('weekRate');
          const monthRate = getFieldValue('monthRate');

          if (!dailyRate && !weekRate && !monthRate) {
            return <span className="my-0 text-error">{t('Please fill in at least one of the rates!')}</span>;
          }

          return null;
        }}
      </Form.Item>

      <Divider className="mb-6 mt-0" />

      <Form.Item
        label={t('Booking Description')}
        name="description"
        rules={[
          () => ({
            async validator(_, value) {
              const isValidDescription = validateLocalizedJSON(value);

              if (!value || isValidDescription) {
                await Promise.resolve();
                return;
              }
              return await Promise.reject(new Error(t('Description must be a valid localized JSON')));
            },
          }),
        ]}
      >
        <TextArea placeholder={t('Add description...')} bordered rows={3} defaultValue={defaultLocalizedDescription} />
      </Form.Item>

      <Form.Item
        label={t('Size Guide Description')}
        name="extendedDescription"
        rules={[
          () => ({
            async validator(_, value) {
              const isValidDescription = validateLocalizedJSON(value);

              if (!value || isValidDescription) {
                await Promise.resolve();
                return;
              }
              return await Promise.reject(new Error(t('Description must be a valid localized JSON')));
            },
          }),
        ]}
      >
        <TextArea placeholder={t('Add description...')} bordered rows={3} defaultValue={defaultLocalizedDescription} />
      </Form.Item>

      {selectedWarehouseId && (
        <>
          <Divider className="mb-4 mt-0" />

          <Form.Item name="discountKeys" label={t('Promotions')}>
            <CheckboxGroup name="discountKeys" options={rentOptionOptions} className="flex flex-col gap-2" />
          </Form.Item>
        </>
      )}

      <Button type="submit" isLoading={isLoading} isDisabled={isForbidden}>
        {t('Save', { ns: 'common' })}
      </Button>
    </Form>
  );
});
